import {
  Department,
  UserInfo,
  UserSettings,
} from "../../app/javascript/components/__generated__/graphql";
import { PayloadAction, createSelector, createSlice } from "@reduxjs/toolkit";

import { RootState } from "./reduxStore";
import { ZING_CASE_MANAGEMENT_DEPARTMENT_ID } from "../../app/javascript/components/util/zingCaseManagement";

interface AppState {
  currentDepartmentId: number;
  currentUserInfo: UserInfo;
  activeRole: string;
  // Cache of departments loaded via linkedDepartments (for super users)
  linkedDepartmentsCache: Record<number, Department>;
}

const initialState: () => AppState = () => {
  const userSettings: UserSettings = {
    name: null,
    phone: null,
    emailNotificationsEnabled: false,
    emailOnlyUserCreatedOrders: false,
    createdEmailNotificationsEnabled: false,
    inTransitEmailNotificationsEnabled: false,
    deliveredEmailNotificationsEnabled: false,
  };

  return {
    currentDepartmentId: 0,
    activeRole: "",
    currentUserInfo: {
      id: 0,
      email: "",
      role: "",
      roles: [],
      userDepartments: [],
      userSettings: userSettings,
      authProvider: "SPREE",
      permissions: [],
      canViewPayorMembers: false,
    },
    linkedDepartmentsCache: {},
  };
};

const appSlice = createSlice({
  name: "app",
  initialState: initialState(),
  reducers: {
    setCurrentDepartmentId: (
      state: AppState,
      action: PayloadAction<number>,
    ) => {
      localStorage.setItem("departmentId", action.payload.toString());
      state.currentDepartmentId = action.payload;
    },
    setActiveRole: (state: AppState, action: PayloadAction<string>) => {
      localStorage.setItem("activeRole", action.payload);
      state.activeRole = action.payload;
    },
    setCurrentUserInfo: (state: AppState, action: PayloadAction<UserInfo>) => {
      state.currentUserInfo = action.payload;
    },
    setLinkedDepartmentInCache: (
      state: AppState,
      action: PayloadAction<Department>,
    ) => {
      state.linkedDepartmentsCache[action.payload.id] = action.payload;
    },
  },
});

export const {
  setCurrentDepartmentId,
  setActiveRole,
  setCurrentUserInfo,
  setLinkedDepartmentInCache,
} = appSlice.actions;

export const selectIsLoggedIn = (state: RootState) => {
  return state.app.currentUserInfo.id != 0;
};

export const selectAuthProvider = (state: RootState) => {
  return state.app.currentUserInfo.authProvider;
};

export const selectUserRole = (state: RootState) => {
  return state.app.activeRole;
};

export const selectUserEmail = (state: RootState) => {
  return state.app.currentUserInfo.email;
};

export const selectUserInfo = (state: RootState) => {
  return state.app.currentUserInfo;
};

export const selectUserDepartments = (state: RootState) => {
  return state.app.currentUserInfo.userDepartments;
};

export const selectAllAvailableDepartments = (state: RootState) => {
  return state.app.currentUserInfo.userDepartments.concat(
    Object.values(state.app.linkedDepartmentsCache),
  );
};

export const selectDepartmentId = (state: RootState) => {
  return state.app.currentDepartmentId;
};

export const selectUserPermissions = (state: RootState) => {
  return state.app.currentUserInfo.permissions;
};

export const selectCanViewPayorMembers = (state: RootState) => {
  return state.app.currentUserInfo.canViewPayorMembers;
};

// Members needs canViewPayorMembers (the server-side gate) and the Zing case
// management department selected, so a user also in other clinics sees it only
// under Zing.
export const selectShowPayorMembers = (state: RootState) => {
  return (
    selectCanViewPayorMembers(state) &&
    selectDepartmentId(state) === ZING_CASE_MANAGEMENT_DEPARTMENT_ID
  );
};

// Memoized selector to prevent unnecessary re-renders when cache updates
export const selectDepartmentInfo = createSelector(
  [
    (state: RootState) => state.app.currentDepartmentId,
    (state: RootState) => state.app.currentUserInfo.userDepartments,
    (state: RootState) => state.app.linkedDepartmentsCache,
  ],
  (departmentId, userDepartments, linkedDepartmentsCache) => {
    // First check userDepartments (departments user is directly assigned to)
    const fromUserDepartments = userDepartments.find(
      (department) => department.id === departmentId,
    );

    if (fromUserDepartments) {
      return fromUserDepartments;
    }

    // Then check linkedDepartmentsCache (for super user accessible departments)
    return linkedDepartmentsCache[departmentId] || null;
  },
);

export const selectCurrentUserId = (state: RootState) => {
  return state.app.currentUserInfo.id;
};

export const selectHospitalId = (state: RootState) => {
  return selectDepartmentInfo(state)?.hospitalId || 0;
};

export const selectHospitalName = (state: RootState) => {
  return selectDepartmentInfo(state)?.hospitalName || "";
};

export const selectHospitalUsageType = (state: RootState) => {
  return selectDepartmentInfo(state)?.hospitalUsageType || undefined;
};

export const selectIsHomeHealth = (state: RootState) => {
  return selectDepartmentInfo(state)?.isHomeHealth || false;
};

export const selectDepartmentType = (state: RootState) => {
  return selectDepartmentInfo(state)?.typeName || "";
};

export const selectSkipFaxValidation = (state: RootState) => {
  return selectDepartmentInfo(state)?.allowSignersWithoutFaxNumbers || false;
};

export default appSlice.reducer;
